import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Banner } from '../types';
import { bannersApi } from '../lib/api';
import { useSettings } from '../contexts/SettingsContext';
import { normalizeAppRoute } from '../lib/router';
import { getPlaceholderImage } from '../lib/placeholders';

/* ✅ API response type */
interface BannerApiResponse {
  banners: Banner[];
}

export const HeroCarousel: React.FC = () => {
  const { settings } = useSettings();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const fallbackImage =
    settings?.heroLogo ||
    settings?.logo ||
    getPlaceholderImage(1600, 900, 'Silver Strix');
  const fallbackSlides: Banner[] = [
    {
      id: 'fallback-logo',
      title: settings?.storeName || 'Silver Strix',
      subtitle: settings?.tagline || 'Fragrance for every journey',
      image_url: fallbackImage,
      link_url: '/products',
      button_text: 'Shop Now',
    },
  ];
  const slides = banners.length ? banners : fallbackSlides;

  useEffect(() => {
    const fetchBanners = async () => {
      const { data, error } = (await bannersApi.getAll({
        position: 'hero',
        active: true,
      })) as {
        data?: BannerApiResponse;
        error?: unknown;
      };

      if (!error && data?.banners?.length) {
        setBanners(
          data.banners.map((b) => ({
            id: b.id || (b as Banner & { _id?: string })._id || b.title,
            title: b.title || settings?.storeName || 'Silver Strix',
            subtitle: b.subtitle || settings?.tagline || 'Fragrance for every journey',
            image_url: b.image_url || fallbackImage,
            link_url: normalizeAppRoute(b.link_url || '/'),
            button_text: b.button_text || 'Shop Now',
          }))
        );
      } else {
        setBanners([]);
      }
    };

    fetchBanners();
  }, [fallbackImage, settings?.storeName, settings?.tagline]);

  useEffect(() => {
    if (!slides.length) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % slides.length);

  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section className="group relative min-h-[620px] w-full overflow-hidden bg-background lg:h-screen">
      <div className="absolute inset-0 bg-text" />

      {slides.map((banner, index) => (
        <div
          key={banner.id || `${banner.title}-${index}`}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="relative w-full h-full">
            <img
              src={banner.image_url}
              alt={banner.title}
              className={`h-full w-full ${banners.length ? 'object-cover' : 'object-contain scale-[0.9] md:scale-[0.96]'}`}
            />

            <div className={`absolute inset-0 ${banners.length ? 'bg-[linear-gradient(90deg,rgba(13,13,13,0.76)_0%,rgba(13,13,13,0.46)_42%,rgba(13,13,13,0.16)_100%)]' : 'bg-[linear-gradient(90deg,rgba(13,13,13,0.9)_0%,rgba(74,44,42,0.72)_48%,rgba(13,13,13,0.2)_100%)]'} flex items-center`}>
              <div className="container mx-auto px-4 lg:px-8">
                <div className="max-w-3xl space-y-5 text-background animate-fade-in">
                  {!banners.length && (
                    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-accent">
                      {settings?.storeName || 'Silver Strix'}
                    </p>
                  )}
                  <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-white/78 backdrop-blur-sm">
                    Curated essentials
                  </div>
                  <h2 className="max-w-2xl text-4xl font-bold leading-[1.05] text-white lg:text-6xl">
                    {banner.title}
                  </h2>
                  <p className="max-w-2xl text-lg font-light leading-8 text-white/82 lg:text-2xl">
                    {banners.length ? banner.subtitle : `${banner.subtitle} ${settings?.tagline || ''}`.trim()}
                  </p>
                  <a href={normalizeAppRoute(banner.link_url)} className="btn-primary mt-2 inline-flex">
                    {banner.button_text}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* ⬅ Prev */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-[rgba(245,240,230,0.88)] p-3 text-text opacity-0 transition group-hover:opacity-100 hover:bg-white"
      >
        <ChevronLeft size={24} />
      </button>

      {/* ➡ Next */}
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-[rgba(245,240,230,0.88)] p-3 text-text opacity-0 transition group-hover:opacity-100 hover:bg-white"
      >
        <ChevronRight size={24} />
      </button>

      {/* ● Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((banner, index) => (
          <button
            key={banner.id || `dot-${index}`}
            onClick={() => setCurrentSlide(index)}
            className={`h-3 rounded-full transition-all ${
              index === currentSlide
                ? 'w-8 bg-accent'
                : 'w-3 bg-white/45'
            }`}
          />
        ))}
      </div>
    </section>
  );
};
